import React from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '../../../components/Navbar';
import {
  DocumentTextIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';

export default function RecordTypeAssignmentPage() {
  const { orgId } = useParams();
  const basePath = orgId ? `/${orgId}` : '';
  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="mb-8">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <Link to={`${basePath}/help`} className="text-gray-500 hover:text-gray-700">
                Help Center
              </Link>
            </li>
            <li className="text-gray-500">/</li>
            <li>
              <Link to={`${basePath}/help/best-practices`} className="text-gray-500 hover:text-gray-700">
                Best Practices
              </Link>
            </li>
            <li className="text-gray-500">/</li>
            <li className="text-gray-900">Record Type Assignment</li>
          </ol>
        </nav>

        {/* Back Button */}
        <Link 
          to={`${basePath}/help/best-practices`} 
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-6"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to Best Practices
        </Link>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Record Type Best Practices
          </h1>
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-green-100 text-green-800">
              Beginner
            </span>
            <span>5 min read</span>
            <span>Data Management</span>
          </div>
        </div>

        {/* Key Concept */}
        <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 mb-8">
          <div className="flex items-start">
            <LightBulbIcon className="h-6 w-6 text-blue-600 mt-0.5" />
            <div className="ml-3">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Golden Rule</h3>
              <p className="text-blue-800">
                Always query Record Types by DeveloperName, never by ID or Name (label).
              </p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">
            Why DeveloperName is Essential
          </h2>
          
          <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">The Problem with IDs and Names</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <span className="text-red-500 mr-2">•</span>
                <span><strong>IDs change</strong> between environments (sandbox vs production)</span>
              </li>
              <li className="flex items-start">
                <span className="text-red-500 mr-2">•</span>
                <span><strong>Names (labels) can be translated</strong> in multi-language orgs</span>
              </li>
              <li className="flex items-start">
                <span className="text-red-500 mr-2">•</span>
                <span><strong>Names can be changed</strong> by admins without updating flows</span>
              </li>
            </ul>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="font-semibold text-gray-900 mb-3">Why DeveloperName Works</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span>Remains constant across all environments</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span>Not affected by translations</span>
              </li>
              <li className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <span>Can only be set once and never changed</span>
              </li>
            </ul>
          </div>
        </section>

        {/* Implementation */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">How to Query Record Types</h2>
          
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Get Records Element Configuration</h3>
            <pre className="bg-gray-100 p-4 rounded text-sm overflow-x-auto">
{`Object: RecordType
Filter Conditions:
  - SObjectType Equals "Account"
  - AND DeveloperName Equals "Business_Account"
  
How Many Records: Only the first record
Store: {!Get_Record_Type}`}
            </pre>
          </div>

          <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-yellow-800 text-sm">
              <strong>Pro tip:</strong> You can make the DeveloperName dynamic by using a formula or variable
              instead of hardcoding it, allowing for even more flexibility.
            </p>
          </div>
        </section>

        {/* Common Use Cases */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Common Use Cases</h2>
          
          <div className="grid gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-2">Creating Records with Specific Type</h4>
              <pre className="bg-white p-3 rounded text-sm mt-2">
{`Create Records: Account
Set Field Values:
  RecordTypeId = {!Get_Record_Type.Id}`}
              </pre>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-2">Dynamic Record Type Selection</h4>
              <pre className="bg-white p-3 rounded text-sm mt-2">
{`Formula: {!RecordTypeName}
IF(Industry = "Healthcare", "Healthcare_Account",
IF(Industry = "Finance", "Financial_Account",
"Standard_Account"))`}
              </pre>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <div className="bg-gray-100 rounded-lg p-6 text-center">
          <p className="text-gray-700 mb-4">
            Ready to fix hardcoded record type IDs in your flows?
          </p>
          <Link 
            to={`${basePath}/flows`} 
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            Analyze Your Flows
          </Link>
        </div>
      </div>
    </div>
  );
}